import { useMemo } from 'react';
import { useTranslation } from '../../../application/hooks/useTranslation';
import type { LedgerFilters, LedgerType } from '../../../application/hooks/useLedgerFilters';
import { trailingMonthKeys } from '../../../domain/calculators/commissions';
import { formatMonthKey } from '../../../domain/formatters/date';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

const TYPES: Array<{ value: LedgerType; labelKey: string }> = [
  { value: '', labelKey: 'commissions.allTypes' },
  { value: 'BOOKING_POOL', labelKey: 'commissions.booking' },
  { value: 'INSTALLMENT_POOL', labelKey: 'commissions.installment' },
];

/** Month chips (last 12 Dhaka months) + month input + type chips + clear. State lives in the URL. */
export function LedgerFilterBar({ filters }: { filters: LedgerFilters }) {
  const { t, locale } = useTranslation();
  const months = useMemo(() => trailingMonthKeys(12).reverse(), []);

  return (
    <form
      className="ledger-filters"
      role="search"
      aria-label={t('commissions.filtersLabel')}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="chip-row" role="group" aria-label={t('commissions.recentMonths')}>
        {months.map((key) => (
          <button
            key={key}
            type="button"
            className="chip tap"
            aria-pressed={filters.month === key}
            onClick={() => filters.setMonth(filters.month === key ? '' : key)}
            data-testid={`month-chip-${key}`}
          >
            {formatMonthKey(key, locale, 'short')}
          </button>
        ))}
      </div>
      <div className="filters">
        <Input
          label={t('commissions.filterMonth')}
          type="month"
          value={filters.month}
          onChange={(e) => filters.setMonth(e.target.value)}
          data-testid="ledger-month"
        />
        <div className="chip-row" role="group" aria-label={t('commissions.filterType')}>
          {TYPES.map(({ value, labelKey }) => (
            <button
              key={value || 'all'}
              type="button"
              className="chip tap"
              aria-pressed={filters.type === value}
              onClick={() => filters.setType(value)}
              data-testid={`type-chip-${value || 'ALL'}`}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
        {filters.active && (
          <Button variant="ghost" onClick={filters.clear} data-testid="ledger-clear">
            {t('commissions.clearFilters')}
          </Button>
        )}
      </div>
    </form>
  );
}
